// src/pages/finance-portal/banking/ManualUploadDialog.js
// QuickBooks-style "Upload bank transactions" dialog.
//
// Five-step wizard:
//   1. Destination   — pick which bank account these transactions land in
//   2. Upload file   — drag/drop CSV
//   3. Map columns   — explicit column mapping with sample data preview,
//                      date-format dropdown. Auto-populated from the
//                      backend's detection on first preview call.
//   4. Preview       — dry-run results: new / duplicate / invalid counts
//                      + per-row sample, inflow/outflow split
//   5. Done          — success summary
//
// The Map Columns step is the differentiator: when the user's CSV uses
// non-standard header names (e.g. "TransDate" instead of "Date") or
// debit/credit split columns, they can remap inline without renaming
// columns in Excel first. The backend honours the `column_overrides`
// payload sent on each preview call so subsequent previews reflect the
// chosen mapping immediately.

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack,
    Typography, Stepper, Step, StepLabel,
    Autocomplete, TextField, MenuItem, FormControl, InputLabel, Select,
    CircularProgress, Alert, Chip, Paper, Table, TableHead, TableBody, TableRow,
    TableCell, Divider, Backdrop, LinearProgress, IconButton, Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const fmtMoney = (v) => {
    if (v == null) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v));
};

const TYPE_COLOR = {
    CHECKING: '#22c55e', SAVINGS: '#22c55e',
    CREDIT: '#f59e0b', LOAN: '#ef4444',
    INVESTMENT: '#8b5cf6', OTHER: '#64748b',
};

const TYPE_ICON = {
    CHECKING: <SavingsIcon />, SAVINGS: <SavingsIcon />,
    CREDIT: <CreditCardIcon />, LOAN: <DescriptionIcon />,
    INVESTMENT: <AccountBalanceIcon />, OTHER: <AccountBalanceIcon />,
};

// Step indices — kept as named constants so the comparisons below
// remain readable as we slot the new Map-columns step in.
const STEP_DESTINATION = 0;
const STEP_UPLOAD      = 1;
const STEP_MAP         = 2;
const STEP_PREVIEW     = 3;
const STEP_DONE        = 4;

const STEP_LABELS = ['Destination', 'Upload CSV', 'Map columns', 'Preview', 'Done'];

// Date-format dropdown options. Each label shows the human-readable
// pattern + an example so a non-developer can pick the right one.
// `value` is the strftime string the backend understands; empty value
// means "auto-detect", which is the default and works for ISO + most
// US formats.
const DATE_FORMAT_OPTIONS = [
    { value: '',          label: 'Auto-detect (try common formats)' },
    { value: '%Y-%m-%d',  label: 'YYYY-MM-DD     (e.g. 2025-03-14)' },
    { value: '%m/%d/%Y',  label: 'MM/DD/YYYY    (US, e.g. 03/14/2025)' },
    { value: '%d/%m/%Y',  label: 'DD/MM/YYYY    (EU, e.g. 14/03/2025)' },
    { value: '%Y/%m/%d',  label: 'YYYY/MM/DD     (e.g. 2025/03/14)' },
    { value: '%m-%d-%Y',  label: 'MM-DD-YYYY     (e.g. 03-14-2025)' },
    { value: '%d-%m-%Y',  label: 'DD-MM-YYYY     (e.g. 14-03-2025)' },
    { value: '%b %d, %Y', label: 'Mon DD, YYYY   (e.g. Mar 14, 2025)' },
    { value: '%d %b %Y',  label: 'DD Mon YYYY    (e.g. 14 Mar 2025)' },
];

export default function ManualUploadDialog({ open, onClose, onImported, accounts }) {
    const [step, setStep] = useState(STEP_DESTINATION);
    const [pickedAccount, setPickedAccount] = useState(null);

    const [file, setFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);
    const [committed, setCommitted] = useState(null);

    // Map-columns state. Auto-populated from the backend's detected
    // mapping on the first preview call, then editable by the user.
    // `useAmountSplit` toggles between "single Amount column" and
    // "separate Debit + Credit columns" — covers both bank export
    // styles in one wizard step.
    const [columnMap, setColumnMap] = useState({
        date: '', desc: '', amount: '', debit: '', credit: '',
        // Optional: the bank's reference / check / wire / ACH number
        // column. Auto-detected from a wide alias list on the backend
        // (REFERENCE_ALIASES) but the user can pick / override here.
        // When present, persisted to StripeTransaction.reference_no
        // and used as a primary signal by the reconciliation matcher.
        reference: '',
    });
    const [useAmountSplit, setUseAmountSplit] = useState(false);
    const [dateFormat, setDateFormat] = useState('');
    const [detectedHeaders, setDetectedHeaders] = useState([]);
    // First 5 raw rows from the CSV, used by the Map-columns step to
    // show "what's in this column" hints. Named distinctly from the
    // `sampleRows` memo below (which is the Preview-step's filtered
    // list of OK rows) to avoid a shadowing collision.
    const [csvSampleRows, setCsvSampleRows] = useState([]);

    const reset = useCallback(() => {
        setStep(STEP_DESTINATION);
        setPickedAccount(null);
        setFile(null);
        setPreview(null);
        setCommitted(null);
        setError(null);
        setColumnMap({ date: '', desc: '', amount: '', debit: '', credit: '',
                       reference: '' });
        setUseAmountSplit(false);
        setDateFormat('');
        setDetectedHeaders([]);
        setCsvSampleRows([]);
    }, []);

    const handleClose = () => {
        if (loading) return;
        onClose?.();
        // Defer reset so the dialog closes smoothly
        setTimeout(reset, 300);
    };

    // --- Step 1: Destination (existing account only) ---

    const canProceedFromStep0 = !!pickedAccount;
    const hasAccounts = (accounts || []).length > 0;

    const goToUpload = () => {
        if (!pickedAccount) return;
        setStep(STEP_UPLOAD);
    };

    // --- Step 2: Upload ---

    const onFilePick = (files) => {
        const f = files && files[0];
        if (!f) return;
        if (!f.name.toLowerCase().endsWith('.csv')) {
            setError('Please upload a .csv file');
            return;
        }
        setError(null);
        setFile(f);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        onFilePick(e.dataTransfer.files);
    };

    // Shared form-data builder. The Map / Preview / Commit paths all
    // need to ship the same payload — column_overrides reflects the
    // user's most recent picks, date_format from the dropdown. Keep
    // the construction in one place so they can't drift.
    const buildFormData = useCallback(() => {
        const form = new FormData();
        form.append('file', file);
        form.append('stripe_account_id', String(pickedAccount.id));
        if (dateFormat) {
            form.append('date_format', dateFormat);
        }
        // Build column_overrides only with the fields the user actually
        // touched — partial overrides are fine, the backend falls back
        // to auto-detect for missing fields.
        const overrides = {};
        if (columnMap.date) overrides.date = columnMap.date;
        if (columnMap.desc) overrides.desc = columnMap.desc;
        if (useAmountSplit) {
            if (columnMap.debit)  overrides.debit  = columnMap.debit;
            if (columnMap.credit) overrides.credit = columnMap.credit;
        } else if (columnMap.amount) {
            overrides.amount = columnMap.amount;
        }
        // Optional reference column — sent only when the user picked one.
        if (columnMap.reference) overrides.reference = columnMap.reference;
        if (Object.keys(overrides).length > 0) {
            form.append('column_overrides', JSON.stringify(overrides));
        }
        return form;
    }, [file, pickedAccount, dateFormat, columnMap, useAmountSplit]);

    // Shared response handler — both success (200) and "missing
    // required columns" (400) come back with the same data envelope
    // (headers_found, sample_rows, mapping) so the Map step can
    // pre-populate either way. axios throws on non-2xx by default, so
    // we catch the 400 in initialDetect and feed the response body
    // through this same path.
    const applyDetectionResponse = useCallback((payload, successFlag) => {
        const data = payload?.data || {};
        const summary = data.summary || {};
        const mapping = summary.mapping || {};
        setDetectedHeaders(summary.headers_found || []);
        setCsvSampleRows(summary.sample_rows || []);
        setColumnMap({
            date:      mapping.date_col      || '',
            desc:      mapping.desc_col      || '',
            amount:    mapping.amount_col    || '',
            debit:     mapping.debit_col     || '',
            credit:    mapping.credit_col    || '',
            reference: mapping.reference_col || '',
        });
        setUseAmountSplit(
            !!(mapping.debit_col || mapping.credit_col) && !mapping.amount_col
        );
        if (successFlag) {
            setPreview(data);
            setError(null);
        } else {
            setPreview(null);
            setError(payload?.message || null);
        }
    }, []);

    // First-call preview: triggered when the user clicks "Next" on the
    // Upload step. Returns the auto-detected mapping + sample rows so
    // we can pre-populate the Map Columns step. The user can then edit
    // the mapping and re-preview before committing.
    const initialDetect = async () => {
        if (!file || !pickedAccount) return;
        setLoading(true);
        setError(null);
        try {
            const form = new FormData();
            form.append('file', file);
            form.append('stripe_account_id', String(pickedAccount.id));
            const res = await axiosInstance.post(
                '/api/finance/stripe/transactions/manual-upload?dry_run=1',
                form, { headers: { 'Content-Type': 'multipart/form-data' } },
            );
            applyDetectionResponse(res.data, !!res.data?.success);
            setStep(STEP_MAP);
        } catch (e) {
            // 400 "missing required columns" still carries
            // headers_found + sample_rows in the response body so the
            // Map step can render useful UI. Pull them out of the
            // error response and proceed; only treat a true crash
            // (network failure, 500) as fatal.
            const body = e?.response?.data;
            if (body && body.data && body.data.summary) {
                applyDetectionResponse(body, false);
                setStep(STEP_MAP);
            } else {
                setError(body?.message || e.message);
            }
        } finally {
            setLoading(false);
        }
    };

    // Re-preview with the user's mapping picks from the Map step.
    const runPreview = async () => {
        if (!file || !pickedAccount) return;
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.post(
                '/api/finance/stripe/transactions/manual-upload?dry_run=1',
                buildFormData(),
                { headers: { 'Content-Type': 'multipart/form-data' } },
            );
            if (!res.data?.success) {
                throw new Error(res.data?.message || 'Preview failed');
            }
            setPreview(res.data.data);
            // Also refresh the mapping echo so the Map step shows what
            // the backend actually used (auto-detect can fill gaps the
            // user left blank).
            const mapping = res.data.data?.summary?.mapping || {};
            setColumnMap((prev) => ({
                date:   columnMap.date   || mapping.date_col   || prev.date,
                desc:   columnMap.desc   || mapping.desc_col   || prev.desc,
                amount: columnMap.amount || mapping.amount_col || prev.amount,
                debit:  columnMap.debit  || mapping.debit_col  || prev.debit,
                credit: columnMap.credit || mapping.credit_col || prev.credit,
            }));
            setStep(STEP_PREVIEW);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    };

    // --- Step 4: Commit ---

    const runCommit = async () => {
        if (!file || !pickedAccount) return;
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.post(
                '/api/finance/stripe/transactions/manual-upload?dry_run=0',
                buildFormData(),
                { headers: { 'Content-Type': 'multipart/form-data' } },
            );
            if (!res.data?.success) {
                throw new Error(res.data?.message || 'Import failed');
            }
            setCommitted(res.data.data);
            setStep(STEP_DONE);
            onImported?.(res.data.data);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    };

    const invalidRows = useMemo(
        () => (preview?.rows || []).filter(r => r.status === 'invalid'),
        [preview]);

    const sampleRows = useMemo(
        () => (preview?.rows || []).filter(r => r.status === 'ok').slice(0, 20),
        [preview]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '14px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                Upload bank transactions
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={step} sx={{ mb: 3 }}>
                    {STEP_LABELS.map((l) =>
                        <Step key={l}><StepLabel sx={{ fontFamily }}>{l}</StepLabel></Step>
                    )}
                </Stepper>

                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {/* STEP 1 — Pick existing account */}
                {step === STEP_DESTINATION && (
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily }}>
                            Which account should these transactions be imported into?
                        </Typography>
                        {!hasAccounts ? (
                            <Alert severity="info">
                                You don't have any bank accounts connected yet. Close this dialog and
                                click <strong>Connect Bank</strong> in Banking Center to add one first.
                            </Alert>
                        ) : (
                            <Autocomplete
                                size="small"
                                options={accounts || []}
                                value={pickedAccount}
                                onChange={(_, val) => setPickedAccount(val)}
                                getOptionLabel={(a) =>
                                    `${a.institution_name || 'Bank'} — ${a.name || a.official_name || ''}${a.mask ? ' •••' + a.mask : ''}`
                                }
                                renderOption={(props, opt) => (
                                    <li {...props} key={opt.id}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                                            <Box sx={{
                                                color: TYPE_COLOR[opt.type] || '#64748b',
                                                display: 'flex',
                                            }}>
                                                {TYPE_ICON[opt.type] || TYPE_ICON.OTHER}
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="body2" sx={{ fontFamily, fontWeight: 600 }}>
                                                    {opt.institution_name || 'Bank'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                                    {opt.name || opt.official_name}{opt.mask ? ` •••${opt.mask}` : ''}
                                                </Typography>
                                            </Box>
                                            {opt.is_manual && (
                                                <Chip size="small" label="Manual" variant="outlined"
                                                      sx={{ fontFamily, fontSize: 10, height: 18 }} />
                                            )}
                                        </Stack>
                                    </li>
                                )}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                renderInput={(p) => <TextField {...p} label="Bank account" placeholder="Pick an account…" />}
                            />
                        )}
                    </Stack>
                )}

                {/* STEP 2 — Upload */}
                {step === STEP_UPLOAD && (
                    <Stack spacing={2}>
                        <Alert
                            severity="info"
                            icon={<DescriptionIcon />}
                            action={
                                <Button
                                    component="a"
                                    href={`${process.env.PUBLIC_URL || ''}/templates/bank-transactions-template.csv`}
                                    download="bank-transactions-template.csv"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    sx={{
                                        fontFamily, fontWeight: 600, whiteSpace: 'nowrap',
                                        color: ACCENT, '&:hover': { bgcolor: `${ACCENT}14` },
                                    }}
                                >
                                    Sample CSV
                                </Button>
                            }
                        >
                            Your CSV must have a header row with these columns (case-insensitive):
                            <strong> Date, Description, Amount</strong>. Debit/Credit split columns also
                            work. Positive amounts = money leaving the account (outflows).
                        </Alert>
                        <Box
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            sx={{
                                border: `2px dashed ${dragOver ? ACCENT : '#cbd5e1'}`,
                                borderRadius: 2, p: 5, textAlign: 'center',
                                cursor: 'pointer',
                                bgcolor: dragOver ? `${ACCENT}10` : '#fafafa',
                                transition: 'all 0.15s',
                            }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 48, color: dragOver ? ACCENT : '#94a3b8', mb: 1 }} />
                            <Typography sx={{ fontFamily, fontWeight: 600 }}>
                                {file ? file.name : 'Drop your CSV file here or click to browse'}
                            </Typography>
                            {file && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                    {(file.size / 1024).toFixed(1)} KB
                                </Typography>
                            )}
                            <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden
                                onChange={(e) => onFilePick(e.target.files)} />
                        </Box>

                        {pickedAccount && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                Importing into: <strong>{pickedAccount.institution_name || 'Bank'} —
                                {' '}{pickedAccount.name || pickedAccount.official_name}</strong>
                                {pickedAccount.mask && ` (•••${pickedAccount.mask})`}
                            </Typography>
                        )}
                    </Stack>
                )}

                {/* STEP 3 — Map columns (QuickBooks-style) */}
                {step === STEP_MAP && (
                    <Stack spacing={2}>
                        <Alert severity="info" icon={<AutoAwesomeIcon />}>
                            We auto-detected your column layout — confirm or
                            change the mapping below. The sample-data preview
                            shows what's in each column so you can pick the
                            right one even when the headers don't match
                            standard names.
                        </Alert>

                        {/* Column-mapping grid. Each canonical field gets a
                            dropdown over the actual CSV headers, plus a
                            small "first sample value" hint underneath so
                            non-technical users can verify the pick by
                            looking at real data. */}
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                <ColumnPickerRow
                                    label="Date"
                                    required
                                    value={columnMap.date}
                                    onChange={(v) =>
                                        setColumnMap((m) => ({ ...m, date: v }))
                                    }
                                    headers={detectedHeaders}
                                    sampleRows={csvSampleRows}
                                    helper="The transaction date column."
                                />
                                <ColumnPickerRow
                                    label="Description"
                                    required
                                    value={columnMap.desc}
                                    onChange={(v) =>
                                        setColumnMap((m) => ({ ...m, desc: v }))
                                    }
                                    headers={detectedHeaders}
                                    sampleRows={csvSampleRows}
                                    helper="The payee / memo / narrative column."
                                />

                                {/* Amount style toggle — single column vs
                                    split debit/credit columns. Picking the
                                    right style first then mapping is the
                                    quickbooks-style flow. */}
                                <FormControl size="small">
                                    <InputLabel>Amount column style</InputLabel>
                                    <Select
                                        label="Amount column style"
                                        value={useAmountSplit ? 'split' : 'single'}
                                        onChange={(e) =>
                                            setUseAmountSplit(e.target.value === 'split')
                                        }
                                    >
                                        <MenuItem value="single">
                                            Single Amount column
                                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                (positive = outflow, negative = inflow — bank convention)
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem value="split">
                                            Separate Debit + Credit columns
                                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                (one column per direction)
                                            </Typography>
                                        </MenuItem>
                                    </Select>
                                </FormControl>

                                {!useAmountSplit && (
                                    <ColumnPickerRow
                                        label="Amount"
                                        required
                                        value={columnMap.amount}
                                        onChange={(v) =>
                                            setColumnMap((m) => ({ ...m, amount: v }))
                                        }
                                        headers={detectedHeaders}
                                        sampleRows={csvSampleRows}
                                        helper="Positive = money out, negative = money in."
                                    />
                                )}
                                {useAmountSplit && (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <Box sx={{ flex: 1 }}>
                                            <ColumnPickerRow
                                                label="Debit (money out)"
                                                icon={<ArrowUpwardIcon fontSize="small" sx={{ color: '#ef4444' }} />}
                                                value={columnMap.debit}
                                                onChange={(v) =>
                                                    setColumnMap((m) => ({ ...m, debit: v }))
                                                }
                                                headers={detectedHeaders}
                                                sampleRows={csvSampleRows}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <ColumnPickerRow
                                                label="Credit (money in)"
                                                icon={<ArrowDownwardIcon fontSize="small" sx={{ color: '#10b981' }} />}
                                                value={columnMap.credit}
                                                onChange={(v) =>
                                                    setColumnMap((m) => ({ ...m, credit: v }))
                                                }
                                                headers={detectedHeaders}
                                                sampleRows={csvSampleRows}
                                            />
                                        </Box>
                                    </Stack>
                                )}

                                <Divider />

                                {/* Optional Reference / Check / Wire-number
                                    column. Auto-detected via header aliases
                                    (Reference, Check No, Trace Number, etc.)
                                    but the user can pick / override here.
                                    When set, persisted on the txn and used
                                    by the reconciler as a primary match
                                    signal alongside the description. */}
                                <ColumnPickerRow
                                    label="Reference / check no."
                                    value={columnMap.reference}
                                    onChange={(v) =>
                                        setColumnMap((m) => ({ ...m, reference: v }))
                                    }
                                    headers={detectedHeaders}
                                    sampleRows={csvSampleRows}
                                    helper="Optional. Strongest match signal — invoices with this number land cleanly."
                                />

                                <Divider />

                                <FormControl size="small">
                                    <InputLabel>Date format</InputLabel>
                                    <Select
                                        label="Date format"
                                        value={dateFormat}
                                        onChange={(e) => setDateFormat(e.target.value)}
                                    >
                                        {DATE_FORMAT_OPTIONS.map((o) => (
                                            <MenuItem key={o.value || 'auto'} value={o.value}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Paper>

                        {/* Raw sample table — shows the first 5 CSV rows
                            EXACTLY as they appear in the file. Helps the
                            user verify their column picks against real
                            data instead of guessing from headers. */}
                        {csvSampleRows.length > 0 && detectedHeaders.length > 0 && (
                            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                <Box sx={{
                                    px: 2, py: 1,
                                    borderBottom: '1px solid rgba(15,23,42,0.08)',
                                    background: '#f8fafc',
                                }}>
                                    <Typography variant="caption" sx={{
                                        fontFamily, fontWeight: 700, color: '#475569',
                                    }}>
                                        Sample data (first {csvSampleRows.length} rows
                                        from your CSV)
                                    </Typography>
                                </Box>
                                <Box sx={{ maxHeight: 200, overflowY: 'auto', overflowX: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {detectedHeaders.map((h) => {
                                                    const isMapped =
                                                        columnMap.date === h ||
                                                        columnMap.desc === h ||
                                                        columnMap.amount === h ||
                                                        columnMap.debit === h ||
                                                        columnMap.credit === h;
                                                    return (
                                                        <TableCell
                                                            key={h}
                                                            sx={{
                                                                fontWeight: 700,
                                                                fontFamily,
                                                                whiteSpace: 'nowrap',
                                                                bgcolor: isMapped
                                                                    ? `${ACCENT}14`
                                                                    : undefined,
                                                                color: isMapped
                                                                    ? ACCENT
                                                                    : undefined,
                                                            }}
                                                        >
                                                            {h}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {csvSampleRows.map((row, idx) => (
                                                <TableRow key={idx}>
                                                    {detectedHeaders.map((h) => (
                                                        <TableCell
                                                            key={h}
                                                            sx={{
                                                                fontFamily,
                                                                whiteSpace: 'nowrap',
                                                                fontSize: 12,
                                                                color: '#475569',
                                                            }}
                                                        >
                                                            {row[h] != null
                                                                ? String(row[h])
                                                                : ''}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            </Paper>
                        )}
                    </Stack>
                )}

                {/* STEP 4 — Preview */}
                {step === STEP_PREVIEW && preview && (
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip color="primary" label={`${preview.summary.new_count} new`} />
                            <Chip color="default" label={`${preview.summary.duplicate_count} duplicates skipped`} />
                            <Chip color={preview.summary.invalid_count > 0 ? 'warning' : 'default'}
                                  label={`${preview.summary.invalid_count} invalid`} />
                            <Chip variant="outlined"
                                  label={`${preview.summary.total_rows} total rows`} />
                        </Stack>
                        <Divider />

                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                            Column mapping: Date ← <strong>{preview.summary.mapping.date_col || '—'}</strong>,
                            Description ← <strong>{preview.summary.mapping.desc_col || '—'}</strong>,
                            Amount ← <strong>
                                {preview.summary.mapping.amount_col
                                 || `${preview.summary.mapping.debit_col} / ${preview.summary.mapping.credit_col}`}
                            </strong>
                        </Typography>

                        {sampleRows.length > 0 && (
                            <Paper variant="outlined" sx={{ maxHeight: 240, overflowY: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sampleRows.map((r) => (
                                            <TableRow key={r.row_number}>
                                                <TableCell>{r.row_number}</TableCell>
                                                <TableCell>{r.date}</TableCell>
                                                <TableCell>{r.description}</TableCell>
                                                <TableCell align="right" sx={{
                                                    color: r.amount > 0 ? '#ef4444' : '#10b981', fontWeight: 600,
                                                }}>
                                                    {r.amount > 0 ? '−' : '+'}{fmtMoney(Math.abs(r.amount))}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {r.duplicate
                                                        ? <Chip size="small" label="Duplicate" variant="outlined" />
                                                        : <Chip size="small" color="success" label="New" />}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}

                        {invalidRows.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff7ed' }}>
                                <Typography variant="caption" sx={{ fontFamily, fontWeight: 700, color: '#9a3412' }}>
                                    {invalidRows.length} row{invalidRows.length === 1 ? '' : 's'} will be skipped:
                                </Typography>
                                <Box sx={{ maxHeight: 120, overflowY: 'auto', mt: 0.5 }}>
                                    {invalidRows.map(r => (
                                        <Typography key={r.row_number} variant="caption" sx={{ display: 'block', fontFamily }}>
                                            Row {r.row_number}: {r.reason}
                                        </Typography>
                                    ))}
                                </Box>
                            </Paper>
                        )}
                    </Stack>
                )}

                {/* STEP 5 — Done */}
                {step === STEP_DONE && committed && (
                    <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
                        <CheckCircleIcon sx={{ color: ACCENT, fontSize: 64 }} />
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>
                            Imported {committed.summary.committed_count}
                            {' '}transaction{committed.summary.committed_count === 1 ? '' : 's'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily, textAlign: 'center' }}>
                            They're sitting in the <strong>For Review</strong> tab of Banking Center,
                            ready to categorize and post to the General Ledger.
                            {committed.summary.duplicate_count > 0 &&
                                ` ${committed.summary.duplicate_count} duplicate row(s) were skipped.`}
                            {committed.summary.invalid_count > 0 &&
                                ` ${committed.summary.invalid_count} invalid row(s) were skipped.`}
                        </Typography>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading}>
                    {step === STEP_DONE ? 'Close' : 'Cancel'}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {step === STEP_DESTINATION && (
                    <Button variant="contained" onClick={goToUpload}
                            disabled={!canProceedFromStep0}
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        Next
                    </Button>
                )}
                {step === STEP_UPLOAD && (
                    <>
                        <Button onClick={() => setStep(STEP_DESTINATION)} disabled={loading}>Back</Button>
                        <Button variant="contained" onClick={initialDetect}
                                disabled={!file || loading}
                                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                            Next: map columns
                        </Button>
                    </>
                )}
                {step === STEP_MAP && (
                    <>
                        <Button onClick={() => setStep(STEP_UPLOAD)} disabled={loading}>Back</Button>
                        <Button variant="contained" onClick={runPreview}
                                disabled={loading
                                    || !columnMap.date
                                    || !columnMap.desc
                                    || (!useAmountSplit && !columnMap.amount)
                                    || (useAmountSplit
                                        && !columnMap.debit
                                        && !columnMap.credit)}
                                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                            Preview import
                        </Button>
                    </>
                )}
                {step === STEP_PREVIEW && (
                    <>
                        <Button onClick={() => setStep(STEP_MAP)} disabled={loading}>
                            Back to mapping
                        </Button>
                        <Button variant="contained" onClick={runCommit}
                                disabled={loading || !preview?.summary?.new_count}
                                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                            Import {preview?.summary?.new_count || 0}
                            {' '}transaction{preview?.summary?.new_count === 1 ? '' : 's'}
                        </Button>
                    </>
                )}
            </DialogActions>

            {/* Backdrop during commit so the user can see why the dialog is frozen */}
            <Backdrop
                open={loading && step === STEP_PREVIEW}
                sx={{ position: 'absolute', zIndex: 1, color: '#fff', bgcolor: 'rgba(15,23,42,0.3)' }}
            >
                <Stack alignItems="center" spacing={1.5}>
                    <CircularProgress sx={{ color: '#fff' }} />
                    <Typography sx={{ fontFamily, color: '#fff' }}>Importing…</Typography>
                </Stack>
            </Backdrop>
        </Dialog>
    );
}


/**
 * One row of the Map-columns step. Renders:
 *   - A canonical-field label (Date, Description, Amount, etc.)
 *   - A dropdown over the actual CSV column headers detected by the
 *     backend.
 *   - A small "first sample value" hint underneath, pulled from the
 *     first sample row, so non-technical users can verify their pick
 *     against real data.
 *
 * Used inside the Map step; extracted out of the main render to keep
 * the wizard JSX readable.
 */
function ColumnPickerRow({
    label, required, value, onChange, headers, sampleRows, helper, icon,
}) {
    const firstSample = useMemo(() => {
        if (!value || !sampleRows || sampleRows.length === 0) return null;
        for (const row of sampleRows) {
            const v = row[value];
            if (v != null && String(v).trim() !== '') return String(v);
        }
        return null;
    }, [value, sampleRows]);

    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <Box sx={{
                minWidth: { sm: 180 },
                display: 'flex', alignItems: 'center', gap: 0.75,
            }}>
                {icon}
                <Typography variant="body2" sx={{ fontFamily, fontWeight: 600 }}>
                    {label}
                </Typography>
                {required && (
                    <Typography component="span" sx={{ color: '#ef4444', fontWeight: 700 }}>*</Typography>
                )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormControl size="small" fullWidth>
                    <Select
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        displayEmpty
                        renderValue={(v) => v || <em style={{ color: '#94a3b8' }}>— Pick a column —</em>}
                    >
                        <MenuItem value=""><em>— Not used —</em></MenuItem>
                        {headers.map((h) => (
                            <MenuItem key={h} value={h}>{h}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {firstSample && (
                    <Typography variant="caption" sx={{
                        fontFamily, color: '#64748b', display: 'block', mt: 0.25,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }} title={firstSample}>
                        sample: <Box component="code" sx={{
                            fontFamily: 'monospace', color: '#334155',
                            bgcolor: '#f1f5f9', px: 0.5, borderRadius: 0.5,
                        }}>{firstSample}</Box>
                    </Typography>
                )}
                {!firstSample && helper && (
                    <Typography variant="caption" sx={{
                        fontFamily, color: '#94a3b8', display: 'block', mt: 0.25,
                    }}>
                        {helper}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
}
